<script setup lang="ts">
/**
 * BlockRenderer
 *
 * Dynamic component that renders content blocks based on their type.
 * Maps block types to their corresponding components and passes data as props.
 * Components are lazy-loaded for better code splitting.
 *
 * Automatically includes StickyMobileCta when a hero block has showForm enabled.
 */
import { type Component, defineAsyncComponent, hydrateOnVisible } from 'vue'

import type { BlockType, ContentBlock } from '~~/shared/types'

// Lazy-load StickyMobileCta (not in blockComponents map as it's not a content block)
const StickyMobileCta = defineAsyncComponent(() => import('./cta/StickyMobileCta.vue'))

interface Props {
  blocks: ContentBlock[]
  /**
   * Page service context (e.g. "Residential CCTV"). Required when the page
   * renders blocks across multiple BlockRenderer instances (e.g. the CCTV
   * switcher path splits off the hero), since a slice without the hero block
   * cannot derive it on its own. When omitted, it falls back to the hero
   * block within `blocks`.
   */
  service?: string
}

const props = defineProps<Props>()

// Map block types to lazy-loaded components
const blockComponents: Record<BlockType, Component> = {
  // Heroes
  hero: defineAsyncComponent(() => import('./heroes/HeroSplit.vue')),
  'hero-gradient': defineAsyncComponent(() => import('./heroes/HeroGradient.vue')),
  'hero-index': defineAsyncComponent(() => import('./heroes/HeroServiceIndex.vue')),
  'hero-service': defineAsyncComponent(() => import('./heroes/HeroService.vue')),

  // Content sections
  'split-content': defineAsyncComponent(() => import('./content/SectionSplitContent.vue')),
  'multi-split': defineAsyncComponent(() => import('./content/SectionMultiSplit.vue')),
  checklist: defineAsyncComponent(() => import('./content/SectionChecklist.vue')),
  'nested-checklist': defineAsyncComponent(() => import('./content/SectionNestedChecklist.vue')),
  'industry-pills': defineAsyncComponent(() => import('./content/SectionIndustryPills.vue')),
  'anchor-nav': defineAsyncComponent(() => import('./content/SectionAnchorNav.vue')),
  'service-preview': defineAsyncComponent(() => import('./content/SectionServicePreview.vue')),
  'comparison-table': defineAsyncComponent({
    loader: () => import('./content/SectionComparisonTable.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '100px' }),
  }),
  'pricing-cards': defineAsyncComponent(() => import('./content/SectionPricingCards.vue')),
  'monitoring-banner': defineAsyncComponent(() => import('./content/SectionMonitoringBanner.vue')),
  'compliance-cards': defineAsyncComponent(() => import('./content/SectionComplianceCards.vue')),
  'article-content': defineAsyncComponent(() => import('./content/SectionArticleContent.vue')),
  'benefits-list': defineAsyncComponent(() => import('./content/SectionBenefitsList.vue')),
  'contact-directory': defineAsyncComponent(() => import('./content/SectionContactDirectory.vue')),
  'contact-split': defineAsyncComponent(() => import('./content/SectionContactSplit.vue')),
  'document-links': defineAsyncComponent(() => import('./content/SectionDocumentLinks.vue')),
  'stats-bar': defineAsyncComponent(() => import('./content/SectionStatsBar.vue')),

  // Grids
  'icon-cards': defineAsyncComponent(() => import('./grids/GridIconCards.vue')),
  'image-cards': defineAsyncComponent(() => import('./grids/GridImageCards.vue')),
  'audience-icons': defineAsyncComponent(() => import('./grids/GridAudienceIcons.vue')),
  'service-links': defineAsyncComponent(() => import('./grids/GridServiceLinks.vue')),
  'icon-index': defineAsyncComponent(() => import('./grids/GridIconIndex.vue')),
  'image-index': defineAsyncComponent(() => import('./grids/GridImageIndex.vue')),
  'service-areas-grid': defineAsyncComponent(() => import('./grids/GridServiceAreasIndex.vue')),
  'support-cards': defineAsyncComponent(() => import('./grids/GridSupportCards.vue')),
  'related-guides': defineAsyncComponent(() => import('./grids/GridRelatedGuides.vue')),

  // CTAs
  'cta-banner': defineAsyncComponent(() => import('./cta/BannerCta.vue')),
  'compact-cta': defineAsyncComponent(() => import('./cta/BannerCompactCta.vue')),
  'final-cta': defineAsyncComponent(() => import('./cta/BannerFinalCta.vue')),

  // Interactive (defer hydration until visible: radix-vue CollapsibleContent
  // runs an on-mount geometry read that causes forced reflow in the LCP window
  // when this below-the-fold block hydrates eagerly).
  accordion: defineAsyncComponent({
    loader: () => import('./interactive/SectionAccordion.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '100px' }),
  }),
  'tabbed-faq': defineAsyncComponent(() => import('./content/SectionTabbedFaq.vue')),
  testimonials: defineAsyncComponent(() => import('./interactive/SectionTestimonials.vue')),
}

/**
 * Get the component for a block type
 */
function getComponent(type: BlockType): Component | null {
  return blockComponents[type] || null
}

/**
 * Hero service context (e.g. "Residential CCTV"). Prefers the explicit `service`
 * prop (set by the page so it survives a hero-less block slice), falling back to
 * the hero block within `blocks`. Injected into the CTA block props below and
 * passed to StickyMobileCta so residential pages route their quote CTAs to the
 * residential-cctv-quote modal variant (Company field hidden).
 */
const heroService = computed(() => {
  if (props.service) return props.service
  const hero = props.blocks.find((block) => block.type === 'hero')
  return hero?.data?.service ?? ''
})

/**
 * Page-level intent derived from the hero's primaryCta.href (?need=X). Passed to
 * StickyMobileCta so the mobile sticky quote button fires the explicit Sydney /
 * city intent instead of the generic resolveQuoteIntent fallback.
 */
const { extractIntent } = useIntentModal()
const heroQuoteIntent = computed(() => {
  const hero = props.blocks.find((block) => block.type === 'hero') as
    | { data?: { primaryCta?: { href?: string } } }
    | undefined
  return extractIntent(hero?.data?.primaryCta?.href ?? '') ?? ''
})

/** Block types whose quote CTA should inherit the hero's service context */
const CONTEXT_AWARE_CTA_TYPES = new Set<BlockType>(['cta-banner', 'compact-cta', 'final-cta'])

/**
 * Process block data to decode HTML entities (e.g., &nbsp;) in the title field.
 * Content JSON uses entities for typographic control (orphan prevention),
 * but Vue's {{ }} interpolation renders them as literal text.
 * Also injects the hero service context into CTA blocks that don't set their own.
 */
function getBlockProps(block: ContentBlock) {
  const data = { ...block.data, sectionBg: block.sectionBg, visible: block.visible }
  if (data.title) data.title = decodeEntities(data.title)
  if (CONTEXT_AWARE_CTA_TYPES.has(block.type)) {
    const d = data as Record<string, unknown>
    d.service = d.service ?? heroService.value
  }
  return data
}

/**
 * Sticky-positions the wrapper for an opt-in sticky anchor-nav. Applied to the
 * wrapper (not inside the block) because its parent spans the page, giving the
 * sticky element room to follow; top matches the 84px site header.
 */
function wrapperClass(block: ContentBlock): string {
  if (block.type === 'anchor-nav' && (block.data as { sticky?: boolean })?.sticky) {
    return 'sticky top-[84px] z-40'
  }
  return ''
}

/**
 * Find the hero block (if any) - supports hero, hero-gradient, hero-index, and hero-service
 */
const heroBlock = computed(() => {
  return props.blocks.find((block) =>
    block.type === 'hero' || block.type === 'hero-gradient' || block.type === 'hero-index' || block.type === 'hero-service'
  )
})

/**
 * Check if page has a hero with form enabled
 * Used to determine if StickyMobileCta should be shown
 * Note: hero-index never has a form, so this only applies to regular hero
 */
const hasHeroWithForm = computed(() => {
  const hero = props.blocks.find((block) => block.type === 'hero')
  return hero?.data?.showForm === true
})

/**
 * Get the hero element ID for Intersection Observer
 */
const heroElementId = computed(() => {
  if (!heroBlock.value) return 'hero'
  return heroBlock.value.anchorId ?? `block-${heroBlock.value.id}`
})

</script>

<template>
  <div class="service-page-content">
    <template v-for="block in blocks" :key="block.id">
      <!-- Wrapper div for scroll targeting - prevents attribute inheritance warnings -->
      <div v-if="getComponent(block.type)" :id="block.anchorId ?? `block-${block.id}`" :class="wrapperClass(block)">
        <component
          :is="getComponent(block.type)"
          v-bind="getBlockProps(block)"
        />
      </div>

      <!-- Development warning for unknown block types -->
      <div
        v-else-if="$config.public.isDev"
        class="bg-red-50 p-8 text-center text-red-600"
      >
        Unknown block type: {{ block.type }}
      </div>
    </template>

    <!-- Sticky mobile CTA (only shown when hero has form) -->
    <ClientOnly>
      <StickyMobileCta
        v-if="hasHeroWithForm"
        :hero-id="heroElementId"
        :service="heroService"
        :quote-intent="heroQuoteIntent"
      />
    </ClientOnly>
  </div>
</template>